import {
  html,
  SYSBaseElement,
  TemplateResult,
} from "../../../../../../_ovlclient/src"
import Chart from "chart.js/auto"
import { T } from "../../../../../../_ovlclient/src/textapi"
import { FeatureGet } from "../../../../../../_ovlclient/src/global/globalapi"
import {
  ProcessStart,
  ProcessEnd,
  ProcessIsRunning,
} from "../../../../../../_ovlclient/src/library/longrunningprocesstracker/processapi"

export interface KPIProps {
  kpiCode: string
  viewType: string
  filterCardCode?: string
}

export class CompKpi extends SYSBaseElement {
  props!: KPIProps
  descriptionExpanded: boolean = false

  private chartInstances: Chart[] = []
  private lastResult: any = null

  async init() {
    ProcessStart("KPIDataRefresh")
    try {
      await this.actions.app.features.KPI.actions.FetchKPIData({ 
        kpiCode: this.props.kpiCode,
        viewType: this.props.viewType,
        filterCardCode: this.props.filterCardCode
      })
    } finally {
      ProcessEnd("KPIDataRefresh")
    }
  }

  async handleManualRefresh() {
    if (ProcessIsRunning("KPIDataRefresh")) return
    await this.init()
  }

  async afterRender() {
    const viewData = this.state.app.features.KPI.data[this.props.viewType]
    const kpiState = viewData ? viewData[this.props.kpiCode] : null

    if (kpiState && kpiState.result && kpiState.result !== this.lastResult) {
      this.lastResult = kpiState.result
      this.renderCharts(kpiState.result)
    }
  }

  renderCharts(result: any) {
    const dataObj = result.data || result.Data
    if (!result || !dataObj) return

    const rType = (result.renderType || result.RenderType || "").toLowerCase()
    
    // Destroy previous instances to allow clean re-render
    this.chartInstances.forEach(chart => chart.destroy())
    this.chartInstances = []

    if (rType === "bar") {
      const canvas = this.querySelector(`#mainChart_${this.props.kpiCode}`) as HTMLCanvasElement
      if (canvas) {
        const chart = this.renderSingleChart(canvas, result)
        if (chart) this.chartInstances.push(chart)
      }
    } 
    else if (rType === "pie") {
      const datasets = dataObj.datasets || dataObj.Datasets || []
      datasets.forEach((ds: any, index: number) => {
        const canvas = this.querySelector(`#pieChart_${this.props.kpiCode}_${index}`) as HTMLCanvasElement
        if (canvas) {
          const chart = this.renderPieChart(canvas, ds, result)
          if (chart) this.chartInstances.push(chart)
        }
      })
    }
  }

  renderSingleChart(canvas: HTMLCanvasElement, result: any): Chart | null {
    const dataObj = result.data || result.Data
    if (!canvas || !dataObj) return null
    
    const categories = dataObj.categories || dataObj.Categories
    const datasets = dataObj.datasets || dataObj.Datasets || []
    
    const translatedCategories = categories ? categories.map((c: string) => T(c)) : []
    const defaultPalette = ["#005A9C", "#E35500", "#E4A700", "#2B7D2B", "#6A6D70"]

    const chartDatasets = datasets.map((ds: any, index: number) => {
      const typeStr = ds.type || ds.Type || "bar"
      const type = typeStr.toLowerCase()
      const label = ds.label || ds.Label
      const values = ds.values || ds.Values
      const color = ds.color || ds.Color
      
      const defaultColor = defaultPalette[index % defaultPalette.length]

      return {
        label: T(label),
        data: values,
        type: type,
        backgroundColor: color || (type === "line" ? "transparent" : defaultColor),
        borderColor: color || defaultColor,
        borderWidth: type === "line" ? 2 : 0,
      }
    })

    return new Chart(canvas, {
      type: "bar",
      data: {
        labels: translatedCategories,
        datasets: chartDatasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: 10
        },
        plugins: {
          legend: {
            position: "bottom",
            labels: {
                boxWidth: 12
            }
          }
        },
        scales: {
            x: {
                ticks: {
                    autoSkip: false,
                    maxRotation: 45,
                    minRotation: 0
                }
            }
        }
      }
    })
  }

  renderPieChart(canvas: HTMLCanvasElement, dataset: any, result: any): Chart | null {
    const dataObj = result.data || result.Data
    if (!canvas || !dataObj) return null

    const categories = dataObj.categories || dataObj.Categories
    const translatedCategories = categories ? categories.map((c: string) => T(c)) : []
    const defaultColors = ["#005A9C", "#E35500", "#E4A700", "#2B7D2B", "#6A6D70"]

    const label = dataset.label || dataset.Label
    const values = dataset.values || dataset.Values
    const colors = dataset.colors || dataset.Colors

    return new Chart(canvas, {
      type: "pie",
      data: {
        labels: translatedCategories,
        datasets: [{
          data: values,
          backgroundColor: colors || defaultColors,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: 10
        },
        plugins: {
          title: {
            display: true,
            text: T(label)
          },
          legend: {
            position: "bottom",
            labels: {
                boxWidth: 12
            }
          }
        }
      }
    })
  }

  renderTextIndicator(result: any) {
    const dataObj = result.data || result.Data
    const primaryValue = dataObj.primaryValue || dataObj.PrimaryValue
    const unit = dataObj.unit || dataObj.Unit
    const secondaryValue = dataObj.secondaryValue || dataObj.SecondaryValue
    const secondaryLabel = dataObj.secondaryLabel || dataObj.SecondaryLabel
    const secondaryValue2 = dataObj.secondaryValue2 || dataObj.SecondaryValue2
    const secondaryLabel2 = dataObj.secondaryLabel2 || dataObj.SecondaryLabel2
    const secondaryValue3 = dataObj.secondaryValue3 || dataObj.SecondaryValue3
    const secondaryLabel3 = dataObj.secondaryLabel3 || dataObj.SecondaryLabel3
    
    return html`
      <div class="kpi-text-indicator">
        <div class="kpi-text-primary">${primaryValue}</div>
        ${unit ? html`<div class="kpi-text-unit">${T(unit)}</div>` : ""}
        ${secondaryValue ? html`
          <div class="kpi-text-secondary">
            ${T(secondaryLabel)}: ${secondaryValue}
          </div>
        ` : ""}
        ${secondaryValue2 ? html`
          <div class="kpi-text-secondary">
            ${T(secondaryLabel2)}: ${secondaryValue2}
          </div>
        ` : ""}
        ${secondaryValue3 ? html`
          <div class="kpi-text-secondary">
            ${T(secondaryLabel3)}: ${secondaryValue3}
          </div>
        ` : ""}
      </div>
    `
  }

  renderContent() {
    const viewData = this.state.app.features.KPI.data[this.props.viewType]
    const kpiState = viewData ? viewData[this.props.kpiCode] : null

    if (!kpiState || kpiState.loading) {
      return html`<div class="fd-busy-indicator--m kpi-loading-indicator" aria-hidden="false" aria-label="Loading"></div>`
    }
    if (kpiState.error) {
      return html`<div class="fd-message-strip fd-message-strip--error fd-message-strip--no-icon kpi-error-strip" role="alert">
        <p class="fd-message-strip__text">${kpiState.error}</p>
      </div>`
    }
    const result = kpiState.result
    if (!result) {
      return html`<div>No data available</div>`
    }

    const dataObj = result.data || result.Data
    const rType = (result.renderType || result.RenderType || "").toLowerCase()

    if (rType === "text") {
      return this.renderTextIndicator(result)
    }

    if (rType === "bar") {
      return html`<div class="kpi-chart-container"><canvas id="mainChart_${this.props.kpiCode}"></canvas></div>`
    }

    if (rType === "pie") {
      const datasets = dataObj?.datasets || dataObj?.Datasets || []
      return html`
        <div class="kpi-charts-container">
          ${datasets.map((_: any, i: number) => html`
            <div class="kpi-chart-wrapper">
              <canvas id="pieChart_${this.props.kpiCode}_${i}"></canvas>
            </div>
          `)}
        </div>
      `
    }

    return html`<div>Unsupported Render Type: ${result.renderType || result.RenderType}</div>`
  }

  renderDateRange() {
    const viewData = this.state.app.features.KPI.data[this.props.viewType]
    const kpiState = viewData ? viewData[this.props.kpiCode] : null

    if (!kpiState || !kpiState.result) return ""

    const result = kpiState.result
    const from = result.dateRangeFrom || result.DateRangeFrom
    const to = result.dateRangeTo || result.DateRangeTo
    if (!from || !to) return ""

    const formatDate = (dateStr: string) => {
      const d = new Date(dateStr)
      return d.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' })
    }

    const kpiFeature = FeatureGet("KPIUser")
    let showPreviousYear = false
    if (kpiFeature.Found()) {
      const kpiConfigs = kpiFeature.GetJson1Parsed() as { [key: string]: { [key: string]: any } }
      const viewConfig = kpiConfigs[this.props.viewType]
      showPreviousYear = viewConfig && viewConfig[this.props.kpiCode]?.showPreviousYear === true
    }

    let prevRange = html``
    if (showPreviousYear) {
      const dFrom = new Date(from)
      const dTo = new Date(to)
      dFrom.setFullYear(dFrom.getFullYear() - 1)
      dTo.setFullYear(dTo.getFullYear() - 1)
      prevRange = html`
        <div class="kpi-date-range-item">
            <strong>${T("KPILabel_PreviousYear")}:</strong> ${formatDate(dFrom.toISOString())} - ${formatDate(dTo.toISOString())}
        </div>
      `
    }

    return html`
      <div class="kpi-date-info">
        <div class="kpi-date-range-item">
            <strong>${T("KPILabel_Current")}:</strong> ${formatDate(from)} - ${formatDate(to)}
        </div>
        ${prevRange}
      </div>
    `
  }

  async getUI(): Promise<TemplateResult> {
    const title = this.props.kpiCode + "Title"
    const desc = this.props.kpiCode + "Description"

    return html`
      <div class="fd-layout-panel kpi-tile">
        <div class="fd-layout-panel__header kpi-header-container">
            <div class="kpi-title-bar">
                <div class="kpi-title-click-area" @click=${() => { this.descriptionExpanded = !this.descriptionExpanded; this.doRender(); }}>
                    <div class="fd-panel__expand">
                        <button
                            class="fd-button fd-button--transparent fd-panel__button"
                            aria-expanded="${this.descriptionExpanded}"
                        >
                            <i class="sap-icon--slim-arrow-${this.descriptionExpanded ? "down" : "right"}"></i>
                        </button>
                    </div>
                    <h2>${T(title)}</h2>
                    <div class="kpi-actions">
                        <button 
                            class="fd-button fd-button--transparent" 
                            @click=${(e: Event) => { e.stopPropagation(); this.handleManualRefresh(); }} 
                            ?disabled=${ProcessIsRunning("KPIDataRefresh")}
                            aria-label="Refresh"
                        >
                            <i class="sap-icon--refresh"></i>
                        </button>
                    </div>                </div>
            </div>
            <div class="kpi-description-wrapper" ?hidden=${!this.descriptionExpanded}>
                <p class="kpi-description">${T(desc)}</p>
                ${this.renderDateRange()}
            </div>
        </div>
        <div class="fd-layout-panel__body kpi-content">
            ${this.renderContent()}
        </div>
      </div>
    `
  }
}
